/* eslint-disable react/jsx-max-props-per-line */
/**
 * Modal alinhado ao tema v2 (`src/theme_v2`: `createTheme`, paleta `neutral` / `primary`, light & dark).
 * Usa `useTheme()` do contexto MUI — nas rotas `/v2` o provider já injeta esse tema.
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

const getConfirmColor = (theme, color) =>
  (theme.palette[color] && theme.palette[color].main) || theme.palette.primary.main;

const DialogDefaultV2 = ({
  open,
  handlerCancel,
  handlerConfirm,
  title = '',
  cancelMessage = 'Cancelar',
  fullScreen = false,
  useFullScreen = true,
  disabledConfirmButton = false,
  confirmLoading = false,
  confirmLoadingMessage = 'Reprocessando…',
  confirmMessage = 'Confirmar',
  confirmColor = 'primary',
  confirmVariant = 'contained',
  fullWidth = false,
  maxWidth = 'sm',
  children,
  contentClassName,
  readOnly = false,
  headerAction = null,
  extraActions = null,
  paperScrollPaperClassName = '',
  zIndex,
}) => {
  const theme = useTheme();
  const shouldFullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isFullScreen =
    (shouldFullscreen && useFullScreen) || fullScreen;

  const { neutral, primary, mode } = theme.palette;
  const headerBg =
    mode === 'dark'
      ? `linear-gradient(145deg, ${neutral[900]} 0%, ${neutral[800]} 55%, ${alpha(neutral[700], 0.92)} 100%)`
      : `linear-gradient(145deg, ${neutral[800]} 0%, ${neutral[700]} 45%, ${alpha(primary.main, 0.12)} 100%)`;
  const headerBorder = `1px solid ${alpha(mode === 'dark' ? neutral[50] : neutral[900], 0.08)}`;
  const contentBg =
    mode === 'dark'
      ? alpha(neutral[900], 0.45)
      : neutral[100];
  const actionsBg =
    mode === 'dark'
      ? alpha(neutral[800], 0.85)
      : neutral[50];

  /** Fecha só pelos botões (Cancelar / Confirmar / extraActions / headerAction). Backdrop e ESC não disparam fechamento. */
  const handleDialogClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handlerCancel(event, reason);
  };

  const renderHeaderActions = () => {
    if (!headerAction) return null;
    const list = Array.isArray(headerAction) ? headerAction : [headerAction];
    return list.map((action, i) => {
      if (action.show === false) return null;
      return (
        <Button
          key={i}
          color="primary"
          disabled={action.disabled}
          onClick={action.onClick}
          size="small"
          variant="contained"
          sx={{
            boxShadow: (t) =>
              action.disabled
                ? 'none'
                : `0 2px 8px ${alpha(t.palette.primary.main, 0.35)}`,
            fontWeight: 600,
            px: 1.75,
            py: 0.5,
            transition: t => t.transitions.create(['box-shadow', 'transform'], {
              duration: t.transitions.duration.short,
            }),
            '&:hover:not(:disabled)': {
              boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.45)}`,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {action.title}
        </Button>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      fullScreen={isFullScreen}
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogTitle
        sx={{
          background: headerBg,
          borderBottom: headerBorder,
          boxSizing: 'border-box',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2 },
          position: 'relative',
          '&::after': {
            background: `linear-gradient(90deg, ${primary.main} 0%, ${alpha(primary.main, 0.35)} 55%, transparent 100%)`,
            bottom: 0,
            content: '""',
            height: 3,
            left: 0,
            opacity: 0.9,
            position: 'absolute',
            width: '38%',
          },
        }}
      >
        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 2 }}
        >
          <Typography
            component="span"
            sx={{
              color: theme.palette.common.white,
              fontWeight: 600,
              letterSpacing: 0.3,
              pr: { sm: 1 },
              textShadow: `0 1px 2px ${alpha('#000', 0.25)}`,
            }}
            variant="h6"
          >
            {title}
          </Typography>
          {headerAction ? (
            <Stack
              direction="row"
              flexWrap="wrap"
              spacing={1}
              sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
              useFlexGap
            >
              {renderHeaderActions()}
            </Stack>
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent
        className={contentClassName}
        dividers
        sx={{
          backgroundColor: contentBg,
          borderBottom: `1px solid ${alpha(mode === 'dark' ? neutral[100] : neutral[900], 0.1)}`,
          pb: { xs: 2.5, sm: 3.75 },
          pt: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 },
          transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'block',
            animation: open ? 'dialogContentIn 420ms cubic-bezier(0.22, 1, 0.36, 1) both' : 'none',
            '@keyframes dialogContentIn': {
              from: {
                opacity: 0,
                transform: 'translateY(8px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {children}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: actionsBg,
          borderTop: `1px solid ${alpha(mode === 'dark' ? neutral[100] : neutral[900], 0.08)}`,
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: 'flex-end',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {readOnly ? (
          <Button
            color="primary"
            onClick={handlerCancel}
            variant="contained"
            sx={{
              fontWeight: 600,
              minWidth: 120,
              px: 2.5,
            }}
          >
            Fechar
          </Button>
        ) : (
          <>
            <Button
              disabled={confirmLoading}
              onClick={handlerCancel}
              sx={{
                borderColor: alpha(neutral[400], mode === 'dark' ? 0.45 : 0.55),
                color: 'text.primary',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(neutral[500], 0.1),
                  borderColor: neutral[400],
                },
              }}
              variant="outlined"
            >
              {cancelMessage}
            </Button>
            {extraActions &&
              extraActions.map(
                (action, i) =>
                  (action.show === undefined || action.show === true) && (
                    <Button
                      key={i}
                      color={action.color || 'primary'}
                      disabled={action.disabled}
                      onClick={action.onClick}
                      variant={action.variant || 'contained'}
                      sx={{ fontWeight: 600, ...action.sx }}
                    >
                      {action.label}
                    </Button>
                  )
              )}
            <Button
              autoFocus
              color={confirmColor}
              disabled={disabledConfirmButton || confirmLoading}
              onClick={handlerConfirm}
              startIcon={
                confirmLoading ? (
                  <CircularProgress
                    color="inherit"
                    size={16}
                  />
                ) : null
              }
              variant={confirmVariant}
              sx={{
                fontWeight: 700,
                minWidth: 112,
                px: 2.5,
                boxShadow: (t) =>
                  disabledConfirmButton || confirmLoading || confirmVariant !== 'contained'
                    ? 'none'
                    : `0 4px 16px ${alpha(getConfirmColor(t, confirmColor), 0.42)}`,
                '&:hover:not(:disabled)': {
                  boxShadow: (t) =>
                    confirmVariant === 'contained'
                      ? `0 6px 20px ${alpha(getConfirmColor(t, confirmColor), 0.5)}`
                      : 'none',
                  transform: 'translateY(-1px)',
                },
                transition: (t) =>
                  t.transitions.create(['box-shadow', 'transform'], {
                    duration: t.transitions.duration.short,
                  }),
              }}
            >
              {confirmLoading ? confirmLoadingMessage : confirmMessage}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

DialogDefaultV2.propTypes = {
  cancelMessage: PropTypes.string,
  confirmMessage: PropTypes.string,
  confirmColor: PropTypes.string,
  confirmVariant: PropTypes.string,
  contentClassName: PropTypes.string,
  children: PropTypes.node,
  fullWidth: PropTypes.bool,
  handlerCancel: PropTypes.func.isRequired,
  handlerConfirm: PropTypes.func.isRequired,
  maxWidth: PropTypes.string,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  readOnly: PropTypes.bool,
  fullScreen: PropTypes.bool,
  headerAction: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  extraActions: PropTypes.array,
  useFullScreen: PropTypes.bool,
  paperScrollPaperClassName: PropTypes.string,
  disabledConfirmButton: PropTypes.bool,
  confirmLoading: PropTypes.bool,
  confirmLoadingMessage: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
};

export default DialogDefaultV2;
